import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, Sparkles, Check, Clock, Search, ExternalLink, Calendar } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: 'Bearer ' + token } : {};
};

const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextGenDate, setNextGenDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const loadPosts = useCallback(async () => {
    try {
      const res = await axios.get(API_BASE + '/api/admin/blog/posts', { headers: getAuthHeader() });
      setPosts(res.data);
      // Load schedule
      try {
        const schedRes = await axios.get(API_BASE + '/api/admin/blog/schedule', { headers: getAuthHeader() });
        setNextGenDate(schedRes.data);
      } catch (e) { console.warn('Blog schedule load skipped:', e.message); }
    } catch (err) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  // Check for approval token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const approveToken = params.get('approve');
    if (approveToken) {
      handleApproveFromUrl(approveToken);
    }
  }, []);

  const handleApproveFromUrl = async (token) => {
    try {
      const res = await axios.get(API_BASE + '/api/blog/approve/' + token);
      toast.success(res.data.message);
      loadPosts();
    } catch (err) {
      toast.error('Failed to approve post');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(API_BASE + '/api/admin/blog/generate', {}, { headers: getAuthHeader() });
      toast.success('Blog post generated! Check your drafts.');
      setPosts(prev => [res.data, ...prev]);
    } catch (err) {
      toast.error('Failed to generate blog post: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (postId) => {
    try {
      await axios.put(API_BASE + '/api/admin/blog/posts/' + postId, { status: 'published' }, { headers: getAuthHeader() });
      toast.success('Post published!');
      loadPosts();
    } catch (err) {
      toast.error('Failed to publish');
    }
  };

  const handleUnpublish = async (postId) => {
    try {
      await axios.put(API_BASE + '/api/admin/blog/posts/' + postId, { status: 'draft' }, { headers: getAuthHeader() });
      toast.success('Post unpublished');
      loadPosts();
    } catch (err) {
      toast.error('Failed to unpublish');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(API_BASE + '/api/admin/blog/posts/' + postId, { headers: getAuthHeader() });
      toast.success('Post deleted');
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSave = async (postData) => {
    try {
      if (editingPost) {
        await axios.put(API_BASE + '/api/admin/blog/posts/' + editingPost.id, postData, { headers: getAuthHeader() });
        toast.success('Post updated');
      } else {
        await axios.post(API_BASE + '/api/admin/blog/posts', postData, { headers: getAuthHeader() });
        toast.success('Post created');
      }
      setShowEditor(false);
      setEditingPost(null);
      loadPosts();
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const filteredPosts = posts.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (showEditor) {
    return <PostEditor post={editingPost} onSave={handleSave} onCancel={() => { setShowEditor(false); setEditingPost(null); }} />;
  }

  return (
    <div className="space-y-6" data-testid="blog-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Blog Module</h2>
          <p className="text-white/50 text-sm">{posts.length} post{posts.length !== 1 ? 's' : ''} &middot; {posts.filter(p => p.status === 'published').length} published</p>
          {nextGenDate && (
            <div className="flex items-center gap-2 mt-2 text-electric-teal/70 text-xs" data-testid="blog-schedule">
              <Calendar className="w-3.5 h-3.5" />
              <span>Next auto-post: <strong className="text-electric-teal">{new Date(nextGenDate.next_generation).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}</strong></span>
              <span className="text-white/20">&middot;</span>
              <span className="text-white/30">{nextGenDate.schedule}</span>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="generate-blog-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Generating...' : 'AI Generate'}
          </Button>
          <Button
            onClick={() => { setEditingPost(null); setShowEditor(true); }}
            className="bg-electric-teal text-primary-dark font-bold"
            data-testid="new-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white placeholder-white/40 focus:border-electric-teal focus:outline-none"
            data-testid="blog-search"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="bg-secondary-dark border border-white/10 rounded-lg text-white px-3 py-2 focus:border-electric-teal focus:outline-none"
          data-testid="blog-filter-status"
        >
          <option value="all">All Status</option>
          <option value="draft">Drafts</option>
          <option value="published">Published</option>
        </select>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="text-white/50 text-center py-12">Loading posts...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12 bg-secondary-dark rounded-lg border border-white/10">
          <p className="text-white/50 mb-4">No blog posts yet</p>
          <p className="text-white/30 text-sm">Click &ldquo;AI Generate&rdquo; to create your first post, or &ldquo;New Post&rdquo; to write one manually.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map(post => (
            <div key={post.id} className="bg-secondary-dark border border-white/10 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={'blog-post-' + post.id}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${post.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {post.status === 'published' ? <Check className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                    {post.status}
                  </span>
                  {post.ai_generated && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                      <Sparkles className="w-3 h-3 mr-1" /> AI
                    </span>
                  )}
                  <span className="text-white/30 text-xs">{post.category}</span>
                </div>
                <h3 className="text-white font-semibold truncate">{post.title}</h3>
                <p className="text-white/40 text-sm truncate">{post.excerpt || post.slug}</p>
                <p className="text-white/30 text-xs mt-1">{new Date(post.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {post.status === 'draft' && (
                  <Button size="sm" onClick={() => handlePublish(post.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={'publish-' + post.id}>
                    <Check className="w-3 h-3 mr-1" /> Publish
                  </Button>
                )}
                {post.status === 'published' && (
                  <Button size="sm" variant="outline" onClick={() => handleUnpublish(post.id)} className="border-white/20 text-white/60 hover:bg-white/5" data-testid={'unpublish-' + post.id}>
                    Unpublish
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setEditingPost(post); setShowEditor(true); }} className="border-white/20 text-white/60 hover:bg-white/5" data-testid={'edit-' + post.id}>
                  <Edit className="w-3 h-3" />
                </Button>
                {post.status === 'published' && (
                  <a href={'/blog/' + post.slug} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="border-white/20 text-white/60 hover:bg-white/5">
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </a>
                )}
                <Button size="sm" variant="outline" onClick={() => handleDelete(post.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={'delete-' + post.id}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const PostEditor = ({ post, onSave, onCancel }) => {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [content, setContent] = useState(post?.content || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [category, setCategory] = useState(post?.category || 'Tips');
  const [tags, setTags] = useState(post?.tags?.join(', ') || '');
  const [metaTitle, setMetaTitle] = useState(post?.meta_title || '');
  const [metaDesc, setMetaDesc] = useState(post?.meta_description || '');
  const [status, setStatus] = useState(post?.status || 'draft');

  const generateSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleTitleChange = (val) => {
    setTitle(val);
    if (!post) setSlug(generateSlug(val));
  };

  const handleSubmit = () => {
    if (!title || !slug || !content) {
      toast.error('Title, slug, and content are required');
      return;
    }
    onSave({
      title, slug, content, excerpt, category,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      meta_title: metaTitle || title,
      meta_description: metaDesc || excerpt,
      status
    });
  };

  return (
    <div className="space-y-6" data-testid="post-editor">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">{post ? 'Edit Post' : 'New Post'}</h2>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="border-white/20 text-white/60" data-testid="cancel-edit-btn">Cancel</Button>
          <Button onClick={handleSubmit} className="bg-electric-teal text-primary-dark font-bold" data-testid="save-post-btn">Save Post</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="text-white/60 text-sm mb-1 block">Title</label>
            <input type="text" value={title} onChange={e => handleTitleChange(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none"
              data-testid="post-title-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Slug</label>
            <input type="text" value={slug} onChange={e => setSlug(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white/70 focus:border-electric-teal focus:outline-none text-sm"
              data-testid="post-slug-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Content (HTML)</label>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={20}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none font-mono text-sm"
              data-testid="post-content-input" />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div>
            <label className="text-white/60 text-sm mb-1 block">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none"
              data-testid="post-status-select">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none"
              data-testid="post-category-select">
              {['Tips', 'Projects', 'Safety', 'Code Updates', 'Energy Efficiency', 'Home Renovation'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Excerpt</label>
            <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={3}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none text-sm"
              data-testid="post-excerpt-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Tags (comma separated)</label>
            <input type="text" value={tags} onChange={e => setTags(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none text-sm"
              data-testid="post-tags-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Meta Title (SEO)</label>
            <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none text-sm"
              data-testid="post-meta-title-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1 block">Meta Description (SEO)</label>
            <textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-secondary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none text-sm"
              data-testid="post-meta-desc-input" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogTab;
